import {
  ILibraryTopology,
  IRegistryOptions,
  ILibraryAssetsPatterns,
  IResourceLoader,
  IRegistryManifestContext,
  IWebpackManifestContext,
  IHostContext,
} from '../registry.types';
import { loadManifests, parseManifest } from '../manifest';
import { createException } from '../errors/RegistryError';

type IAnyManifest =
  | ILibraryTopology
  | IRegistryManifestContext
  | IWebpackManifestContext;

export function getLibrariesArtifactIds(
  libraries: Array<ILibraryTopology | IRegistryManifestContext>,
) {
  return libraries.map(library => {
    if (isRegistryManifestContext(library)) {
      return library.topology.artifactId;
    } else {
      return library.artifactId;
    }
  });
}

export const isLibraryTopology = (
  library: IAnyManifest,
): library is ILibraryTopology => {
  return library.hasOwnProperty('artifactId') && library.hasOwnProperty('url');
};

export const isRegistryManifestContext = (
  library: IAnyManifest,
): library is IRegistryManifestContext => {
  return (
    library.hasOwnProperty('topology') &&
    library.hasOwnProperty('manifest') &&
    (library as IRegistryManifestContext).manifest.hasOwnProperty('version')
  );
};

export const loadLibrariesManifests = async ({
  host,
  resourceLoader,
  libraries,
  options,
}: {
  host: string;
  resourceLoader: IResourceLoader;
  libraries: Array<IAnyManifest>;
  options: IRegistryOptions;
}): Promise<Array<IWebpackManifestContext | IRegistryManifestContext>> => {
  const isCorrectLibraries = libraries.every(
    library => isLibraryTopology(library) || isRegistryManifestContext(library),
  );

  if (!isCorrectLibraries) {
    throw createException('invalid libraries').withHint(
      'Probably wrong "libraries" argument was passed to the registry',
    );
  }

  const topologies = libraries.filter(library =>
    isLibraryTopology(library),
  ) as Array<ILibraryTopology>;

  const registryManifests = libraries.filter(library =>
    isRegistryManifestContext(library),
  ) as Array<IRegistryManifestContext>;

  return [
    ...registryManifests,
    ...[
      // libraries, that should be fetched
      ...(await loadManifests({
        host,
        resourceLoader,
        libraries: topologies,
        options,
      })),
    ],
  ];
};

export function parseManifests({
  host,
  manifests,
  libraryAssetsPatterns,
}: {
  host: string;
  manifests: Array<IWebpackManifestContext | IRegistryManifestContext>;
  libraryAssetsPatterns: ILibraryAssetsPatterns;
}) {
  return manifests
    .map(manifest => parseManifest(manifest, libraryAssetsPatterns))
    .filter(manifest => !!manifest.hosts[host])
    .map<IHostContext>(manifest => {
      return {
        manifest: manifest.hosts[host],
        library: manifest.library,
        assets: manifest.assets,
        statics: manifest.statics,
      };
    });
}
